import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus } from "lucide-react";
import { EASE, fadeUp } from "../../lib/motion";
import { resolveImg } from "../../lib/image";

export const OurEdge = ({ content }) => {
    const e = content?.our_edge || {};
    const items = e.items || [];
    const [active, setActive] = useState(items[0]?.id || null);
    const activeItem = items.find((i) => i.id === active) || items[0];

    return (
        <section id="edge" className="relative py-6 md:py-20" data-testid="our-edge-section">
            <div className="mx-auto max-w-[1240px] px-6 md:px-10">
                <div className="mb-8 max-w-[760px]">
                    <motion.span {...fadeUp(0)} className="text-xs uppercase tracking-[0.24em] text-oth-ink3 font-body">{e.eyebrow}</motion.span>
                    <motion.h2 {...fadeUp(0.1)} className="mt-4 font-display font-semibold text-[40px] sm:text-[52px] md:text-[64px] leading-[1.02] tracking-[-0.035em]">
                        {e.headline}
                    </motion.h2>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                    {/* Accordion */}
                    <div className="lg:col-span-5 flex flex-col gap-3">
                        {items.map((item) => {
                            const open = item.id === active;
                            return (
                                <motion.button
                                    key={item.id}
                                    onClick={() => setActive(item.id)}
                                    layout
                                    transition={{ duration: 0.8, ease: EASE }}
                                    className={`text-left w-full rounded-[28px] p-6 md:p-7 transition-colors duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] ${
                                        open ? "bg-[#0A0A0A] text-white" : "bg-[#EFEFEA] text-oth-ink hover:bg-[#ECEBE7]"
                                    }`}
                                    data-testid={`edge-accordion-${item.id}`}
                                >
                                    <div className="flex items-center justify-between gap-4">
                                        <div className="font-display text-xl md:text-2xl font-semibold tracking-tight">{item.title}</div>
                                        <motion.span
                                            animate={{ rotate: open ? 180 : 0 }}
                                            transition={{ duration: 0.6, ease: EASE }}
                                            className={`w-9 h-9 shrink-0 rounded-full flex items-center justify-center ${
                                                open ? "bg-[#0B3B60] text-white" : "bg-white text-oth-ink"
                                            }`}
                                        >
                                            {open ? <Minus size={16} /> : <Plus size={16} />}
                                        </motion.span>
                                    </div>
                                    <AnimatePresence initial={false}>
                                        {open && (
                                            <motion.p
                                                initial={{ height: 0, opacity: 0, marginTop: 0 }}
                                                animate={{ height: "auto", opacity: 1, marginTop: 16 }}
                                                exit={{ height: 0, opacity: 0, marginTop: 0 }}
                                                transition={{ duration: 0.7, ease: EASE }}
                                                className="font-body text-base text-white/72 leading-relaxed max-w-[420px] overflow-hidden"
                                            >
                                                {item.body}
                                            </motion.p>
                                        )}
                                    </AnimatePresence>
                                </motion.button>
                            );
                        })}
                    </div>

                    {/* Right visual panel */}
                    <div className="lg:col-span-7">
                        <div className="relative rounded-[32px] overflow-hidden surface-card aspect-[7/6] md:aspect-[5/4] lg:aspect-[4/3]" data-testid="edge-visual-panel">
                            <AnimatePresence mode="wait">
                                <motion.img
                                    key={activeItem?.id}
                                    src={resolveImg(activeItem?.image)}
                                    alt={activeItem?.title}
                                    initial={{ opacity: 0, scale: 1.04 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.98 }}
                                    transition={{ duration: 1.1, ease: EASE }}
                                    className="absolute inset-0 w-full h-full object-cover"
                                />
                            </AnimatePresence>
                            <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0) 50%, rgba(0,0,0,0.35) 100%)" }} />
                            <AnimatePresence mode="wait">
                                <motion.div
                                    key={activeItem?.id + "-caption"}
                                    initial={{ y: 16, opacity: 0 }}
                                    animate={{ y: 0, opacity: 1 }}
                                    exit={{ y: -10, opacity: 0 }}
                                    transition={{ duration: 0.8, ease: EASE }}
                                    className="absolute left-6 right-6 bottom-6 md:left-8 md:bottom-8 text-white"
                                >
                                    <div className="text-xs uppercase tracking-[0.22em] text-white/70 font-body">Currently viewing</div>
                                    <div className="mt-2 font-display text-2xl md:text-3xl font-semibold">{activeItem?.title}</div>
                                </motion.div>
                            </AnimatePresence>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};
